import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ban, History, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { errorText } from './SimpleModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const when = (iso) => new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
const box = { background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.25rem', marginBottom: '1.25rem' };
const STATUS = { open: ['Aguardando análise', '#fef3c7', '#92400e'], confirmed: ['Suspensão confirmada', '#fed7d7', '#9b2c2c'], reverted: ['Revertida', '#c6f6d5', '#22543d'] };

const SuspensionItem = ({ r, onDecide, busy }) => {
  const [label, bg, color] = STATUS[r.status];
  return (
    <div data-testid={`suspension-request-${r.id}`} style={{ borderBottom: '1px solid #f1f5f9', padding: '0.75rem 0', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 320px', fontSize: '0.88rem', color: '#2D3748' }}>
        <div><strong>{r.nome}</strong> ({r.email}) · editora <strong>{r.editor_nome}</strong></div>
        <div style={{ color: '#4a5568', marginTop: 4 }}>Motivo: {r.motivo}</div>
        <div style={{ color: '#a0aec0', fontSize: '0.78rem', marginTop: 4 }}>{when(r.created_at)}</div>
      </div>
      <span style={{ background: bg, color, borderRadius: 999, padding: '0.2rem 0.7rem', fontSize: '0.78rem', fontWeight: 600 }} data-testid={`suspension-status-${r.id}`}>{label}</span>
      {r.status !== 'reverted' && (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onDecide(r, 'revert')} data-testid={`suspension-revert-${r.id}`}>Reverter</Button>
          {r.status === 'open' && <Button size="sm" disabled={busy} onClick={() => onDecide(r, 'confirm')} style={{ background: '#e53e3e' }} data-testid={`suspension-confirm-${r.id}`}>Confirmar</Button>}
        </div>
      )}
    </div>
  );
};

const ProfileLogs = ({ logs }) => (
  <div style={box} data-testid="admin-profile-logs">
    <h3 style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}><History className="w-4 h-4" />Alterações de cadastro das editoras</h3>
    {!logs.length ? <p style={{ color: '#a0aec0', fontSize: '0.85rem' }}>Nenhuma alteração registrada.</p> : logs.map((l) => (
      <div key={l.id} data-testid={`profile-log-${l.id}`} style={{ borderBottom: '1px solid #f1f5f9', padding: '0.6rem 0', fontSize: '0.85rem' }}>
        <div><strong>{l.editor_nome}</strong> ({l.editor_email}) · <span style={{ color: '#a0aec0' }}>{when(l.at)}</span></div>
        <ul style={{ margin: '4px 0 0 1rem', color: '#4a5568', listStyle: 'disc' }}>
          {l.changes.map((c) => <li key={c.field}>{c.label}: <s style={{ color: '#a0aec0' }}>{c.old || '—'}</s> → <strong>{c.new || '—'}</strong></li>)}
        </ul>
      </div>
    ))}
  </div>
);

export const AdminRequests = ({ onCountChange }) => {
  const [reqs, setReqs] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([
    axios.get(`${API}/admin/suspension-requests`, { headers: auth() }).then((r) => { setReqs(r.data.requests); onCountChange?.(r.data.open); }),
    axios.get(`${API}/admin/profile-logs`, { headers: auth() }).then((r) => setLogs(r.data.logs)),
  ]).catch(() => toast.error('Erro ao carregar solicitações'));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const decide = async (r, action) => {
    if (!window.confirm(action === 'revert' ? `Reverter a suspensão de ${r.nome}? O acesso volta na hora.` : `Confirmar a suspensão de ${r.nome}?`)) return;
    setBusy(true);
    try { await axios.put(`${API}/admin/suspension-requests/${r.id}`, { action }, { headers: auth() }); toast.success('Pedido atualizado'); load(); } catch (e) { toast.error(errorText(e, 'Erro ao atualizar')); } finally { setBusy(false); }
  };
  if (!reqs) return <Loader2 className="w-6 h-6 animate-spin" />;
  return (
    <div data-testid="admin-requests">
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2D3748', marginBottom: '1.25rem' }}>Solicitações das editoras</h1>
      <div style={box} data-testid="admin-suspension-requests">
        <h3 style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><Ban className="w-4 h-4" />Suspensões de assinantes</h3>
        <p style={{ fontSize: '0.8rem', color: '#718096', marginBottom: 8 }}>O assinante já fica suspenso quando a editora envia o pedido. Confirme ou reverta.</p>
        {!reqs.length ? <p style={{ color: '#a0aec0', fontSize: '0.85rem' }}>Nenhum pedido de suspensão.</p> : reqs.map((r) => <SuspensionItem key={r.id} r={r} onDecide={decide} busy={busy} />)}
      </div>
      <ProfileLogs logs={logs} />
    </div>
  );
};
